import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import itkLogo from "../assets/itk_logo.png";
import analyticGraphic from "../assets/analytic-graphic.png";
import inovationGraphic from "../assets/inovation-center-graphic.png";
import Header from "../components/Header";
import {
  LayoutDashboard,
  Lightbulb,
  ClipboardCheck,
  BookOpenCheck,
  GraduationCap,
  BadgePercent,
  ChevronDown,
  Sparkles,
  ArrowRight,
} from "lucide-react";

const PRIMARY_BLUE = "#1565C0";
const HEADER_HEIGHT = 72;
const HEADER_TOP_OFFSET = 12;
const MOBILE_HEADER_CLEARANCE = HEADER_HEIGHT + HEADER_TOP_OFFSET + 16;
const MOBILE_MENU_TOP = HEADER_HEIGHT + HEADER_TOP_OFFSET + 8;

const BG = "#f4f7fb";
const CARD_BG = "#ffffff";
const BORDER = "#dbe3ee";
const TEXT_DARK = "#0f172a";
const TEXT_MUTED = "#64748b";

const TYPO = {
  fontFamily:
    '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  weights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  sizes: {
    xs: 12,
    sm: 13,
    md: 14,
    lg: 16,
    xl: 24,
  },
  lineHeights: {
    tight: 1.25,
    normal: 1.45,
    relaxed: 1.6,
  },
  letterSpacing: {
    tight: "-0.01em",
    normal: "0",
  },
};

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < breakpoint;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function StaffPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(768);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);

  const menuGroups = useMemo(
    () => [
      {
        key: "itk-analitik",
        title: "İTK Analitik",
        icon: LayoutDashboard,
        children: [
          { label: "Yoklama", path: "/yoklama", icon: ClipboardCheck },
          { label: "Ödev", path: "/odev", icon: BookOpenCheck },
          { label: "YKS", path: "/yks", icon: GraduationCap },
          { label: "LGS", path: "/lgs", icon: BadgePercent },
        ],
      },
      {
        key: "inovasyon-merkezi",
        title: "İnovasyon Merkezi",
        icon: Lightbulb,
        children: [
          {
            label: "İTK Starter",
            path: "/itk-starter",
            icon: Sparkles,
          },
        ],
      },
    ],
    []
  );

  const findPathMatch = (groups, pathname) => {
    for (const group of groups) {
      for (const child of group.children || []) {
        if (child.path === pathname) {
          return { groupKey: group.key, childLabel: child.label };
        }
        for (const sub of child.children || []) {
          if (sub.path === pathname) {
            return {
              groupKey: group.key,
              childLabel: child.label,
              subLabel: sub.label,
            };
          }
        }
      }
    }
    return null;
  };

  const initialMatch = findPathMatch(menuGroups, location.pathname);

  const [openGroups, setOpenGroups] = useState(() => ({
    "itk-analitik": true,
    "inovasyon-merkezi": true,
    ...(initialMatch?.groupKey ? { [initialMatch.groupKey]: true } : {}),
  }));

  const [openSubGroups, setOpenSubGroups] = useState(() =>
    initialMatch?.childLabel ? { [initialMatch.childLabel]: true } : {}
  );

  useEffect(() => {
    const match = findPathMatch(menuGroups, location.pathname);
    if (match?.groupKey) {
      setOpenGroups((prev) => ({ ...prev, [match.groupKey]: true }));
    }
    if (match?.childLabel) {
      setOpenSubGroups((prev) => ({ ...prev, [match.childLabel]: true }));
    }
  }, [location.pathname, menuGroups]);

  useEffect(() => {
    if (!isMobile) setIsSidebarOpen(false);
  }, [isMobile]);

  const closeSidebar = () => setIsSidebarOpen(false);

  const isExpanded = isMobile ? true : isSidebarHovered;

  const sidebarStyle = isMobile
    ? {
        ...styles.mobileTopMenu,
        ...(isSidebarOpen ? styles.mobileTopMenuOpen : {}),
      }
    : {
        ...styles.sidebarDesktop,
        width: isExpanded ? "300px" : "96px",
      };

  const contentAreaStyle = isMobile
    ? {
        ...styles.contentArea,
        marginLeft: 0,
      }
    : {
        ...styles.contentArea,
        marginLeft: isExpanded ? "300px" : "96px",
        transition: "margin-left 0.28s ease",
      };

  const hasActiveDescendant = (node) => {
    if (node.path && node.path === location.pathname) return true;
    if (!node.children) return false;
    return node.children.some(hasActiveDescendant);
  };

  const renderChild = (child, depth = 0) => {
    const hasChildren = !!child.children?.length;
    const active = hasActiveDescendant(child);
    const isOpen = !!openSubGroups[child.label];
    const ChildIcon = child.icon || LayoutDashboard;

    if (!hasChildren) {
      return (
        <button
          key={`${child.label}-${child.path}`}
          style={{
            ...styles.subMenuItem,
            ...(active ? styles.subMenuItemActive : {}),
            paddingLeft: depth === 0 ? 14 : 28,
          }}
          onClick={() => {
            navigate(child.path);
            if (isMobile) closeSidebar();
          }}
        >
          <ChildIcon size={16} strokeWidth={2.1} style={styles.subMenuIcon} />
          <span style={styles.subMenuText}>{child.label}</span>
        </button>
      );
    }

    return (
      <div key={child.label}>
        <button
          style={{
            ...styles.nestedToggle,
            ...(active ? styles.nestedToggleActive : {}),
            paddingLeft: depth === 0 ? 14 : 28,
          }}
          onClick={() =>
            setOpenSubGroups((prev) => ({
              ...prev,
              [child.label]: !prev[child.label],
            }))
          }
        >
          <div style={styles.nestedToggleLeft}>
            <ChildIcon size={16} strokeWidth={2.1} style={styles.subMenuIcon} />
            <span style={styles.subMenuText}>{child.label}</span>
          </div>

          <ChevronDown
            size={14}
            strokeWidth={2.2}
            style={{
              ...styles.chevronIcon,
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </button>

        {isOpen && (
          <div style={styles.nestedPanel}>
            {child.children.map((sub) => renderChild(sub, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <Header
        isMobile={isMobile}
        isSidebarOpen={isSidebarOpen}
        onMenuToggle={() => setIsSidebarOpen((prev) => !prev)}
        mobileBgColor={PRIMARY_BLUE}
        mobileLeftCompact={true}
      />

      {isMobile && isSidebarOpen && (
        <div style={styles.mobileOverlay} onClick={closeSidebar} />
      )}

      <main
        style={{
          ...styles.main,
          ...(isMobile ? styles.mainMobile : {}),
        }}
      >
        <aside
          style={sidebarStyle}
          onMouseEnter={() => !isMobile && setIsSidebarHovered(true)}
          onMouseLeave={() => !isMobile && setIsSidebarHovered(false)}
        >
          {!isMobile && (
            <div style={styles.sidebarTop}>
              <div style={styles.logoContainer}>
                <img
                  src={itkLogo}
                  alt="Akademi Analitiği Logo"
                  style={styles.logo}
                />
              </div>
            </div>
          )}

          <div
            style={
              isMobile ? styles.mobileTopMenuScroll : styles.sidebarScroll
            }
          >
            {menuGroups.map((group) => {
              const active = hasActiveDescendant(group);
              const isOpen = !!openGroups[group.key];
              const GroupIcon = group.icon;

              return (
                <div key={group.key} style={styles.groupWrap}>
                  <button
                    style={{
                      ...styles.groupButton,
                      ...(active ? styles.groupButtonActive : {}),
                      justifyContent: isExpanded ? "space-between" : "center",
                    }}
                    onClick={() =>
                      setOpenGroups((prev) => ({
                        ...prev,
                        [group.key]: !prev[group.key],
                      }))
                    }
                    title={group.title}
                  >
                    <div style={styles.groupButtonLeft}>
                      <GroupIcon
                        size={20}
                        strokeWidth={2.1}
                        style={styles.groupIcon}
                      />
                      {isExpanded && (
                        <span style={styles.groupTitle}>{group.title}</span>
                      )}
                    </div>

                    {isExpanded && (
                      <ChevronDown
                        size={15}
                        strokeWidth={2.2}
                        style={{
                          ...styles.chevronIcon,
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      />
                    )}
                  </button>

                  {isExpanded && isOpen && (
                    <div style={styles.subMenuPanel}>
                      {group.children.map((child) => renderChild(child))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <section style={contentAreaStyle}>
          <div
            style={{
              ...styles.contentContainer,
              ...(isMobile ? styles.contentContainerMobile : {}),
            }}
          >
            <div
              style={{
                ...styles.featureGrid,
                ...(isMobile ? styles.featureGridMobile : {}),
              }}
            >
              <div style={{ ...styles.featureCard, ...styles.premiumCard }}>
                <div style={styles.cardGlow} />
                <img
                  src={analyticGraphic}
                  alt="Dashboard Analitik"
                  style={{
                    ...styles.featureImage,
                    ...(isMobile ? styles.featureImageMobile : {}),
                  }}
                />

                <div style={styles.featureHeader}>
                  <div style={styles.titleBadge}>Veri & Performans</div>
                  <div>
                    <h2 style={styles.featureTitle}>Analitik</h2>
                    <p style={styles.featureSubtitle}>
                      Kurumsal takip ve akademik görünüm ekranları
                    </p>
                  </div>
                </div>

                <div style={styles.shortcutGrid}>
                  <button
                    style={styles.shortcutButton}
                    onClick={() => navigate("/yoklama")}
                  >
                    <span style={styles.shortcutButtonInner}>
                      <ClipboardCheck size={16} strokeWidth={2.1} />
                      Yoklama
                    </span>
                    <ArrowRight size={15} strokeWidth={2.2} />
                  </button>

                  <button
                    style={styles.shortcutButton}
                    onClick={() => navigate("/odev")}
                  >
                    <span style={styles.shortcutButtonInner}>
                      <BookOpenCheck size={16} strokeWidth={2.1} />
                      Ödev
                    </span>
                    <ArrowRight size={15} strokeWidth={2.2} />
                  </button>

                  <button
                    style={styles.shortcutButton}
                    onClick={() => navigate("/yks")}
                  >
                    <span style={styles.shortcutButtonInner}>
                      <GraduationCap size={16} strokeWidth={2.1} />
                      YKS
                    </span>
                    <ArrowRight size={15} strokeWidth={2.2} />
                  </button>

                  <button
                    style={styles.shortcutButton}
                    onClick={() => navigate("/lgs")}
                  >
                    <span style={styles.shortcutButtonInner}>
                      <BadgePercent size={16} strokeWidth={2.1} />
                      LGS
                    </span>
                    <ArrowRight size={15} strokeWidth={2.2} />
                  </button>
                </div>
              </div>

              <div style={{ ...styles.featureCard, ...styles.premiumCard }}>
                <div style={styles.cardGlowSecondary} />
                <img
                  src={inovationGraphic}
                  alt="İnovasyon Merkezi"
                  style={{
                    ...styles.featureImage,
                    ...(isMobile ? styles.featureImageMobile : {}),
                  }}
                />

                <div style={styles.featureHeader}>
                  <div style={styles.titleBadgeSecondary}>Yeni Nesil Ürünler</div>
                  <div>
                    <h2 style={styles.featureTitle}>İnovasyon Merkezi</h2>
                    <p style={styles.featureSubtitle}>
                      Deneysel ürünler ve yeni giriş noktaları
                    </p>
                  </div>
                </div>

                <div style={styles.shortcutGridSingle}>
                  <button
                    style={styles.shortcutButton}
                    onClick={() => navigate("/inovasyon-merkezi")}
                  >
                    <span style={styles.shortcutButtonInner}>
                      <Sparkles size={16} strokeWidth={2.1} />
                      İTK Starter
                    </span>
                    <ArrowRight size={15} strokeWidth={2.2} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: BG,
    fontFamily: TYPO.fontFamily,
    display: "flex",
    flexDirection: "column",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    textRendering: "optimizeLegibility",
    overflowX: "hidden",
  },

  main: {
    flex: 1,
    display: "flex",
    position: "relative",
    padding: "24px",
    paddingTop: "24px",
    marginTop: `${MOBILE_HEADER_CLEARANCE}px`,
    background: BG,
    minWidth: 0,
  },

  mainMobile: {
    padding: "0",
    marginTop: `${MOBILE_HEADER_CLEARANCE}px`,
  },

  sidebarDesktop: {
    position: "fixed",
    top: "0",
    left: 0,
    bottom: 0,
    background: PRIMARY_BLUE,
    color: "#fff",
    borderRight: "1px solid rgba(255,255,255,0.08)",
    boxShadow:
      "24px 0 48px rgba(15, 23, 42, 0.24), 8px 0 18px rgba(15, 23, 42, 0.14)",
    transition: "width 0.28s ease",
    zIndex: 40,
    overflow: "hidden",
  },

  mobileTopMenu: {
    position: "fixed",
    top: `${MOBILE_MENU_TOP}px`,
    left: "16px",
    right: "16px",
    background: PRIMARY_BLUE,
    color: "#fff",
    borderRadius: 16,
    boxShadow:
      "0 18px 40px rgba(15, 23, 42, 0.22), 0 6px 18px rgba(15, 23, 42, 0.14)",
    zIndex: 70,
    overflow: "hidden",
    opacity: 0,
    transform: "translateY(-12px)",
    pointerEvents: "none",
    transition: "all 0.22s ease",
  },

  mobileTopMenuOpen: {
    opacity: 1,
    transform: "translateY(0)",
    pointerEvents: "auto",
  },

  sidebarTop: {
    minHeight: "72px",
    padding: "12px 10px",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottom: "1px solid rgba(255,255,255,0.14)",
    background: PRIMARY_BLUE,
  },

  logoContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "8px 0",
  },

  logo: {
    width: "48px",
    height: "48px",
    objectFit: "contain",
    borderRadius: "14px",
    background: "#ffffff",
    padding: "6px",
  },

  sidebarScroll: {
    height: "calc(100% - 72px)",
    overflowY: "auto",
    padding: "12px 10px 18px",
  },

  mobileTopMenuScroll: {
    padding: "12px",
  },

  groupWrap: {
    marginBottom: 10,
  },

  groupButton: {
    width: "100%",
    minHeight: 48,
    border: "none",
    background: "transparent",
    borderRadius: 12,
    color: "#ffffff",
    padding: "0 12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease",
  },

  groupButtonActive: {
    background: "rgba(255,255,255,0.16)",
    color: "#ffffff",
    boxShadow: "none",
  },

  groupButtonLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  groupIcon: {
    flexShrink: 0,
    color: "#ffffff",
  },

  groupTitle: {
    fontSize: TYPO.sizes.md,
    fontWeight: TYPO.weights.semibold,
    letterSpacing: TYPO.letterSpacing.normal,
    lineHeight: TYPO.lineHeights.tight,
  },

  chevronIcon: {
    color: "#ffffff",
    transition: "transform 0.2s ease",
    flexShrink: 0,
  },

  subMenuPanel: {
    marginTop: 8,
    marginLeft: 8,
    paddingLeft: 10,
    borderLeft: "2px solid rgba(255,255,255,0.18)",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  subMenuItem: {
    minHeight: 40,
    border: "none",
    borderRadius: 10,
    background: "transparent",
    color: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 10,
    textAlign: "left",
    fontSize: TYPO.sizes.sm,
    fontWeight: TYPO.weights.medium,
    letterSpacing: TYPO.letterSpacing.normal,
    lineHeight: TYPO.lineHeights.normal,
    transition: "all 0.2s ease",
  },

  subMenuItemActive: {
    background: "#ffffff",
    color: PRIMARY_BLUE,
    fontWeight: TYPO.weights.semibold,
  },

  subMenuIcon: {
    flexShrink: 0,
  },

  subMenuText: {
    fontSize: TYPO.sizes.sm,
    fontWeight: TYPO.weights.medium,
    lineHeight: TYPO.lineHeights.normal,
    letterSpacing: TYPO.letterSpacing.normal,
  },

  nestedToggle: {
    width: "100%",
    minHeight: 40,
    border: "none",
    borderRadius: 10,
    background: "transparent",
    color: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    textAlign: "left",
    fontSize: TYPO.sizes.sm,
    fontWeight: TYPO.weights.medium,
    letterSpacing: TYPO.letterSpacing.normal,
    lineHeight: TYPO.lineHeights.normal,
    paddingRight: 10,
    transition: "all 0.2s ease",
  },

  nestedToggleActive: {
    background: "rgba(255,255,255,0.14)",
    color: "#ffffff",
    fontWeight: TYPO.weights.semibold,
  },

  nestedToggleLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },

  nestedPanel: {
    marginTop: 4,
    marginLeft: 10,
    paddingLeft: 6,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  contentArea: {
    flex: 1,
    width: "100%",
    maxWidth: "100%",
    margin: 0,
    background: "transparent",
    borderRadius: 0,
    padding: 0,
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minWidth: 0,
    overflowX: "hidden",
  },

  contentContainer: {
    width: "100%",
    maxWidth: "100%",
    background: "transparent",
    borderRadius: 20,
    padding: "0",
    minHeight: "calc(100vh - 168px)",
    boxShadow: "none",
    border: "none",
    boxSizing: "border-box",
    minWidth: 0,
    overflowX: "hidden",
  },

  contentContainerMobile: {
    padding: "0 5px 10px",
  },

  featureGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 520px) minmax(0, 520px)",
    justifyContent: "center",
    gap: 20,
    width: "100%",
    padding: "0 32px",
    minWidth: 0,
  },

  featureGridMobile: {
    gridTemplateColumns: "1fr",
    padding: "0 10px 8px",
    gap: 12,
  },

  featureCard: {
    position: "relative",
    borderRadius: 24,
    border: `1px solid ${BORDER}`,
    background: CARD_BG,
    padding: "16px 16px 16px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    minWidth: 0,
    overflow: "hidden",
    boxSizing: "border-box",
  },

  premiumCard: {
    boxShadow:
      "0 20px 45px rgba(15, 23, 42, 0.10), 0 10px 22px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255,255,255,0.65)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
    transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
  },

  cardGlow: {
    position: "absolute",
    top: "-60px",
    right: "-40px",
    width: 180,
    height: 180,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(21,101,192,0.14) 0%, rgba(21,101,192,0) 72%)",
    pointerEvents: "none",
  },

  cardGlowSecondary: {
    position: "absolute",
    top: "-50px",
    right: "-20px",
    width: 170,
    height: 170,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(59,130,246,0.12) 0%, rgba(59,130,246,0) 72%)",
    pointerEvents: "none",
  },

  titleBadge: {
    display: "inline-flex",
    alignSelf: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(21,101,192,0.08)",
    color: PRIMARY_BLUE,
    border: "1px solid rgba(21,101,192,0.12)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.01em",
  },

  titleBadgeSecondary: {
    display: "inline-flex",
    alignSelf: "center",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(37,99,235,0.08)",
    color: "#1d4ed8",
    border: "1px solid rgba(37,99,235,0.12)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.01em",
  },

  featureHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 10,
    minWidth: 0,
  },

  featureTitle: {
    margin: 0,
    fontSize: TYPO.sizes.xl,
    fontWeight: TYPO.weights.bold,
    color: PRIMARY_BLUE,
    lineHeight: TYPO.lineHeights.tight,
    textAlign: "center",
    letterSpacing: TYPO.letterSpacing.tight,
    wordBreak: "break-word",
  },

  featureSubtitle: {
    margin: "6px 0 0 0",
    fontSize: TYPO.sizes.md,
    color: TEXT_MUTED,
    fontWeight: TYPO.weights.medium,
    lineHeight: TYPO.lineHeights.normal,
    textAlign: "center",
    wordBreak: "break-word",
  },

  shortcutGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 12,
    marginBottom: "8px",
    minWidth: 0,
  },

  shortcutGridSingle: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 10,
    marginTop: 12,
    marginBottom: "8px",
    minWidth: 0,
  },

  shortcutButton: {
    border: "1px solid rgba(21,101,192,0.18)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,1) 100%)",
    color: PRIMARY_BLUE,
    borderRadius: 14,
    minHeight: 48,
    padding: "0 14px",
    fontSize: TYPO.sizes.sm,
    fontWeight: TYPO.weights.semibold,
    fontFamily: TYPO.fontFamily,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    boxShadow:
      "0 8px 18px rgba(21, 101, 192, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
    minWidth: 0,
  },

  shortcutButtonInner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },

  featureImage: {
    width: "100%",
    height: "180px",
    objectFit: "contain",
    margin: "0 auto 8px auto",
    display: "block",
    filter: "drop-shadow(0 14px 24px rgba(21,101,192,0.10))",
  },

  featureImageMobile: {
    height: "118px",
    margin: "0 auto 4px auto",
  },

  mobileOverlay: {
    position: "fixed",
    top: `${MOBILE_MENU_TOP}px`,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(15, 23, 42, 0.18)",
    zIndex: 65,
  },
};

export default StaffPage;